'use client';

import React, {
    useEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from 'react';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';

export interface PhoneData {
    contactNumber: string;
    countryCode: string;
    isValid: boolean;
}

export interface PhoneInputRef {
    reset: () => void;
    getPhoneData: () => PhoneData;
}

interface Props {
    onChange: (data: PhoneData) => void;
    name?: string;
}

const IntlPhoneInput = forwardRef<PhoneInputRef, Props>(
    ({ onChange, name }, ref) => {
        const inputRef = useRef<HTMLInputElement>(null);
        const itiRef = useRef<any>(null);
        const onChangeRef = useRef(onChange);

        // Sync onChange callback ref
        useEffect(() => {
            onChangeRef.current = onChange;
        }, [onChange]);

        const getPhoneData = useCallback((): PhoneData => {
            if (itiRef.current) {
                const selectedCountryData = itiRef.current.getSelectedCountryData();
                const countryCode = selectedCountryData.dialCode ?? '';

                // Get number and raw value
                let fullNumber = itiRef.current.getNumber() || '';
                let contactNumber = itiRef.current.telInput?.value || inputRef.current?.value || '';

                // Clean contact number (digits only)
                const digitsOnly = contactNumber.replace(/\D/g, '');

                // Validation logic
                let isValidResult = itiRef.current.isValidNumber();

                // FALLBACK: If intl-tel-input isn't ready (returns null or -99 error)
                // or if it's India (+91), enforce the 10-digit rule manually as a fail-safe
                if (isValidResult === null || isValidResult === undefined || itiRef.current.getValidationError() === -99) {
                    // Simple manual validation fallback
                    if (countryCode === '91') {
                        isValidResult = digitsOnly.length === 10;
                    } else {
                        isValidResult = digitsOnly.length >= 7; // Generic baseline
                    }
                } else if (countryCode === '91' && digitsOnly.length !== 10) {
                    // Extra strictness for India if ITI is being too permissive
                    isValidResult = false;
                }

                // Extract national part from full international number
                if (fullNumber && countryCode) {
                    const prefix = '+' + countryCode;
                    if (fullNumber.startsWith(prefix)) {
                        contactNumber = fullNumber.replace(prefix, '').trim();
                    } else if (fullNumber.startsWith(countryCode)) {
                        contactNumber = fullNumber.replace(countryCode, '').trim();
                    }
                }

                // Final cleanup: digits only for the state
                contactNumber = contactNumber.replace(/\D/g, '');

                return { contactNumber, countryCode, isValid: !!isValidResult };
            }
            return { contactNumber: '', countryCode: '', isValid: false };
        }, []);

        const handleChange = useCallback(() => {
            const data = getPhoneData();
            onChangeRef.current(data);
        }, [getPhoneData]);

        useImperativeHandle(ref, () => ({
            reset: () => {
                if (itiRef.current) {
                    itiRef.current.setNumber('');
                }
            },
            getPhoneData: getPhoneData
        }));

        const containerRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            if (inputRef.current) {
                const iti = intlTelInput(inputRef.current, {
                    initialCountry: 'auto',
                    preferredCountries: ['in', 'us', 'gb'],
                    separateDialCode: true,
                    dropdownContainer: containerRef.current || undefined,
                    geoIpLookup: (callback: (countryCode: string) => void) => {
                        fetch('https://ipapi.co/json')
                            .then((res) => res.json())
                            .then((data) => callback(data.country_code))
                            .catch(() => callback('in'));
                    },
                    utilsScript:
                        'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.19/js/utils.js',
                } as any);

                itiRef.current = iti;
                const input = inputRef.current;

                input.addEventListener('input', handleChange);
                input.addEventListener('change', handleChange);
                input.addEventListener('countrychange', handleChange);
                input.addEventListener('keyup', handleChange);

                // Initial syncs to ensure we catch the auto-detected country
                setTimeout(handleChange, 100);
                setTimeout(handleChange, 1000); // Secondary fallback sync

                return () => {
                    input.removeEventListener('input', handleChange);
                    input.removeEventListener('change', handleChange);
                    input.removeEventListener('countrychange', handleChange);
                    input.removeEventListener('keyup', handleChange);
                    iti.destroy();
                };
            }
        }, [handleChange]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
            if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                e.preventDefault();
            }
        };

        return (
            <div className="relative w-full" ref={containerRef}>
                <input
                    placeholder='Your Contact Number'
                    ref={inputRef}
                    name={name || 'contactNumber'}
                    type="tel"
                    onKeyDown={handleKeyDown}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={15}
                    className="w-full h-10 text-sm text-black px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black-500"
                />
            </div>
        );
    }
);

IntlPhoneInput.displayName = 'IntlPhoneInput';
export default IntlPhoneInput;















// 'use client';

// import React, {
//     useEffect,
//     useRef,
//     useImperativeHandle,
//     forwardRef,
// } from 'react';
// import intlTelInput from 'intl-tel-input';
// import 'intl-tel-input/build/css/intlTelInput.css';

// interface PhoneData {
//     contactNumber: string;
//     countryCode: string;
//     isValid: boolean; // <-- add this
// }

// export interface PhoneInputRef {
//     reset: () => void;
// }

// interface Props {
//     onChange: (data: PhoneData) => void;
//     name?: string;
// }

// const IntlPhoneInput = forwardRef<PhoneInputRef, Props>(({ onChange, name }, ref) => {

//     const inputRef = useRef<HTMLInputElement>(null);
//     const itiRef = useRef<any>(null);

//     useImperativeHandle(ref, () => ({
//         reset: () => {
//             if (itiRef.current) {
//                 itiRef.current.setNumber('');
//                 itiRef.current.setCountry(''); // reset selected country (clears it)
//             }
//         },
//     }));

//     console.log('isValid', 55555);

//     useEffect(() => {
//         if (inputRef.current) {
//             itiRef.current = intlTelInput(inputRef.current, {
//                 initialCountry: 'auto',
//                 preferredCountries: ['in', 'us', 'gb'],
//                 separateDialCode: true,
//                 utilsScript:
//                     'https://cdn.jsdelivr.net/npm/intl-tel-input@17.0.19/build/js/utils.js',
//             } as any);

//             inputRef.current.addEventListener('input', handleChange);
//             inputRef.current.addEventListener('change', handleChange);
//             inputRef.current.addEventListener('countrychange', handleChange);
//         }

//         return () => {
//             if (inputRef.current) {
//                 inputRef.current.removeEventListener('input', handleChange);
//                 inputRef.current.removeEventListener('change', handleChange);
//                 inputRef.current.removeEventListener('countrychange', handleChange);
//             }
//         };
//     }, []);

//     const handleChange = () => {
//         if (itiRef.current) {
//             console.log('itiRef', itiRef.current, 6999999);

//             const fullNumber = itiRef.current.getNumber(); // Full international number
//             const selectedCountryData = itiRef.current.getSelectedCountryData();
//             const countryCode = selectedCountryData.dialCode ?? '';
//             const isValid = itiRef.current.isValidNumber();
//             console.log('countryCode', countryCode, 752);

//             console.log(isValid, 733);

//             onChange({
//                 contactNumber: fullNumber.replace(`+${countryCode}`, ''), // Just the national number
//                 countryCode,
//                 isValid,
//             });
//         }
//     };

//     const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
//         const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'];
//         if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
//             e.preventDefault();
//         }
//     };

//     return (
//         <div className="relative">
//             <input
//                 placeholder='Your Contact Number'
//                 ref={inputRef}
//                 name={name || 'contactNumber'}
//                 type="tel"
//                 onKeyDown={handleKeyDown}
//                 inputMode="numeric"
//                 pattern="[0-9]*"
//                 maxLength={15}
//                 className="w-full h-10 text-sm text-black px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black-500"
//             />
//         </div>
//     );
// }
// );

// IntlPhoneInput.displayName = 'IntlPhoneInput';
// export default IntlPhoneInput;